/**
 * Campaign params read off the HOST page URL and forwarded to the iframe.
 *
 * Without this, everything entering through the embedded SDK reached the
 * backend with no attribution: the ad click lands on the merchant's page, the
 * SDK builds its own iframe URL, and `utm_*` never crossed over. Only our own
 * campaign landings kept them, because there the iframe URL *is* the ad's
 * landing URL.
 *
 * Reading the URL at mount time is not enough on its own. The click lands on
 * the merchant's home or campaign page, the visitor then navigates to a model
 * page, and the site drops the params on the way — by the time the brick
 * mounts, `location.search` is clean. Measured on the first day the forwarding
 * shipped: 88 SDK sessions, 0 with a campaign. So the params are captured when
 * the SCRIPT loads and parked in `sessionStorage`, which the mount then falls
 * back to.
 *
 * This only works if `finva.js` is loaded on EVERY page of the merchant's site,
 * not lazily on the page where the brick mounts: a lazy load runs the capture
 * on an already-clean URL. Motoclick did exactly that for weeks (FIN-6) — over
 * 30 days, 740 `credit_app_start` carried a `gclid` (Google lands the click on
 * the model page itself) against 13 with `utm_*` and 8 with `fbclid` (Meta
 * lands on the home). The fix on the merchant side is a `<script src=…finva.js>`
 * in the site-wide layout; see `docs/` for the integration note.
 *
 * `utm_*` stay session-scoped on purpose: `localStorage` would keep attributing
 * visits days later to a campaign the visitor no longer came from.
 *
 * Click ids (`gclid`, `gbraid`, `wbraid`, `fbclid`) are different (FIN-13). We
 * do not decide attribution with them: the backend hands them to Google Ads /
 * Meta as offline conversions, and each platform applies its own attribution
 * window. All we have to do is not lose the click. Per tab and per visit we
 * did: Aug–Oct 2026, PostHog saw 298 solicitudes whose person had a Google ad
 * click in the previous 90 days, and the backend had the click for only 273.
 * 16 of the 25 lost ones had clicked on an EARLIER visit (1 hour to 30 days
 * before) and came back in another tab or another day, with an empty
 * `sessionStorage`. So the last click is also kept in `localStorage`
 * (`finva:click`) for 90 days — the age past which Google rejects a click —
 * and, as a last resort, read back from the ad platforms' own first-party
 * cookies (`_gcl_aw`, `_fbc`). The capture time travels as `click_at`.
 *
 * The iframe already reads these (`lib/credit-application/utm.ts`) and sends
 * them with the registration payload. They travel as query params — never in
 * `InitPayload`, whose contract is frozen. The click ids reach the backend
 * inside `other_url_params` (the iframe URL), which it parses.
 *
 * Parsed by hand instead of with `URLSearchParams` to keep the bundle small,
 * same reasoning as `encodeQueryParam` in `util/dom`.
 */

/**
 * Keys forwarded, in URL order. The five `utm_*` mirror `UTM_KEYS` in
 * `types/credit-application` — keep both in sync. The click ids are not read as
 * structured fields; they ride along so the raw click is still recoverable from
 * `other_url_params`, and PostHog (inside the iframe) picks them up from the
 * iframe URL as event properties on its own.
 *
 * `gbraid` / `wbraid` are what Google Ads sends instead of `gclid` when the
 * click comes from an iOS device with App Tracking Transparency (web-to-app and
 * app-to-web respectively). Without them, iOS Google traffic reads as organic.
 */
export const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
  'gbraid',
  'wbraid',
] as const;

export type CampaignKey = (typeof CAMPAIGN_KEYS)[number];
export type CampaignParams = Partial<Record<CampaignKey, string>>;

/**
 * Epoch ms of when the forwarded click was captured. Ours, never read off the
 * host URL: only sent when a click id is, and only when its time is known.
 */
export const CLICK_AT_KEY = 'click_at';

/** What the iframe URL carries, in order: the campaign, then `click_at`. */
export const FORWARDED_KEYS = [...CAMPAIGN_KEYS, CLICK_AT_KEY] as const;
export type ForwardedParams = CampaignParams & { click_at?: string };

/** Namespaced so it cannot collide with whatever the merchant's site stores. */
export const CAMPAIGN_STORAGE_KEY = 'finva:campaign';

/** `localStorage` key of the last ad click: `{gclid?, gbraid?, wbraid?, fbclid?, at}`. */
export const CLICK_STORAGE_KEY = 'finva:click';

/** Google Ads rejects offline conversions for clicks older than 90 days. */
export const CLICK_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/** Same cap the iframe applies when reading them (`UTM_MAX_LEN`); more would be cut there anyway. */
const UTM_MAX_LEN = 255;

/**
 * Click ids are opaque and long (a real `fbclid` runs past 250 chars), so
 * truncating one corrupts it. The cap is only a guard against a junk URL.
 */
const CLICK_ID_MAX_LEN = 512;

type ClickKey = 'fbclid' | 'gclid' | 'gbraid' | 'wbraid';
type ClickIds = Partial<Record<ClickKey, string>>;
/** The ids of ONE click, and when it was captured (epoch ms) if known. */
type Click = { ids: ClickIds; at?: number };

const CLICK_KEYS: readonly ClickKey[] = ['fbclid', 'gclid', 'gbraid', 'wbraid'];

function isClickKey(key: string): key is ClickKey {
  return (CLICK_KEYS as readonly string[]).includes(key);
}

function maxLenFor(key: CampaignKey): number {
  return isClickKey(key) ? CLICK_ID_MAX_LEN : UTM_MAX_LEN;
}

/**
 * Returns the campaign params present in `search` (defaults to the host page's
 * query). Absent keys are omitted, so a page with no campaign params produces
 * an iframe URL byte-identical to the previous one.
 *
 * A repeated param takes the first value, matching how the iframe reads them.
 */
export function readCampaignParams(search?: string): CampaignParams {
  const raw =
    search ?? (typeof window !== 'undefined' && window.location ? window.location.search : '');
  const out: CampaignParams = {};
  if (!raw) return out;

  for (const pair of raw.replace(/^\?/, '').split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    if (eq < 0) continue;
    const key = decodeParam(pair.slice(0, eq)) as CampaignKey;
    if (!CAMPAIGN_KEYS.includes(key) || out[key] !== undefined) continue;
    // `+` is a space in query strings; `decodeURIComponent` alone leaves it.
    const value = decodeParam(pair.slice(eq + 1).replace(/\+/g, ' ')).trim();
    if (value) out[key] = value.slice(0, maxLenFor(key));
  }
  return out;
}

/** A malformed escape (`%zz`) throws in `decodeURIComponent`; keep the raw text. */
function decodeParam(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Web Storage is not always reachable — Safari private mode, sandboxed frames
 * and cookie-blocking settings throw on mere property access, not just on
 * read/write. This runs while the merchant's page is loading, so anything that
 * escapes here would take their page down with it. Never throws.
 */
function storage(kind: 'sessionStorage' | 'localStorage'): Storage | null {
  try {
    return typeof window !== 'undefined' ? window[kind] ?? null : null;
  } catch {
    return null;
  }
}

/** Stored text is not trusted more than a URL: callers re-validate every field. */
function readJson(store: Storage | null, key: string): Record<string, unknown> | null {
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function writeJson(store: Storage | null, key: string, value: unknown): void {
  if (!store) return;
  try {
    store.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or a blocked store: the visit still works, we just lose the
    // capture if the visitor navigates before opening the widget.
  }
}

/** The click ids in `source`, capped like a URL's. Never mixes in anything else. */
function clickIdsOf(source: Record<string, unknown> | null): ClickIds {
  const ids: ClickIds = {};
  for (const key of CLICK_KEYS) {
    const value = source?.[key];
    if (typeof value === 'string' && value) ids[key] = value.slice(0, CLICK_ID_MAX_LEN);
  }
  return ids;
}

function hasClick(ids: ClickIds): boolean {
  return Object.keys(ids).length > 0;
}

function sameClick(a: ClickIds, b: ClickIds): boolean {
  return CLICK_KEYS.every((key) => a[key] === b[key]);
}

function validTime(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

function isFresh(at: number, now: number): boolean {
  return now - at < CLICK_TTL_MS;
}

/** This tab's capture: the campaign of the visit plus, if it had a click, its time. */
function readSession(): { params: CampaignParams; at?: number } {
  const parsed = readJson(storage('sessionStorage'), CAMPAIGN_STORAGE_KEY);
  if (!parsed) return { params: {} };
  const params: CampaignParams = {};
  for (const key of CAMPAIGN_KEYS) {
    const value = parsed[key];
    if (typeof value === 'string' && value) params[key] = value.slice(0, maxLenFor(key));
  }
  return { params, at: validTime(parsed.at) };
}

/** The last click of any visit, if under 90 days old. An expired one is removed. */
function readStoredClick(now: number): Click | null {
  const store = storage('localStorage');
  const parsed = readJson(store, CLICK_STORAGE_KEY);
  if (!parsed) return null;
  const ids = clickIdsOf(parsed);
  const at = validTime(parsed.at);
  if (hasClick(ids) && at !== undefined && isFresh(at, now)) return { ids, at };
  try {
    store?.removeItem(CLICK_STORAGE_KEY);
  } catch {
    // Blocked store: nothing to clean up that we could reach anyway.
  }
  return null;
}

/**
 * When the click with these ids was first captured, if any record has it. A
 * reload of the landing URL, or the mount on the landing page itself, is the
 * same click — not a new one — so it keeps its original time.
 */
function knownClickTime(ids: ClickIds, now: number): number | undefined {
  const stored = readStoredClick(now);
  if (stored && sameClick(stored.ids, ids)) return stored.at;
  const session = readSession();
  if (session.at !== undefined && sameClick(clickIdsOf(session.params), ids)) return session.at;
  return undefined;
}

/** First value of cookie `name` on the host page, or `undefined`. Never throws. */
function readCookie(name: string): string | undefined {
  let all = '';
  try {
    all = typeof document !== 'undefined' ? document.cookie : '';
  } catch {
    return undefined;
  }
  for (const part of all.split(';')) {
    const eq = part.indexOf('=');
    if (eq > 0 && part.slice(0, eq).trim() === name) return decodeParam(part.slice(eq + 1).trim());
  }
  return undefined;
}

/** A cookie's click id: only base64url text, never longer than a URL's would be. */
const COOKIE_CLICK_ID = /^[\w-]{1,512}$/;

/**
 * Google tag's `_gcl_aw`: `GCL.<unix seconds>.<gclid>`. The tag writes it on
 * the merchant's domain when a gclid lands, so it survives visits where our
 * script was not loaded yet (or `localStorage` was cleared).
 */
function gclCookieClick(): Click | null {
  const match = /^GCL\.(\d+)\.(.+)$/.exec(readCookie('_gcl_aw') ?? '');
  const at = match ? validTime(Number(match[1]) * 1000) : undefined;
  return match && at !== undefined && COOKIE_CLICK_ID.test(match[2])
    ? { ids: { gclid: match[2] }, at }
    : null;
}

/**
 * Meta pixel's `_fbc`: `fb.<subdomain index>.<creation ms>.<fbclid>`. Newer
 * pixels may append more `.`-separated segments after the fbclid; they are not
 * part of it and are dropped.
 */
function fbcCookieClick(): Click | null {
  const parts = (readCookie('_fbc') ?? '').split('.');
  if (parts.length < 4 || parts[0] !== 'fb' || !/^\d+$/.test(parts[1])) return null;
  const at = /^\d+$/.test(parts[2]) ? validTime(Number(parts[2])) : undefined;
  return at !== undefined && COOKIE_CLICK_ID.test(parts[3])
    ? { ids: { fbclid: parts[3] }, at }
    : null;
}

/**
 * Last resort: the ad platforms' own first-party cookies. Each one is a
 * different click, so they are not combined either: the most recent wins,
 * same rule as the stored record. Stale ones (90 days) are ignored.
 */
function cookieClick(now: number): Click | null {
  let best: Click | null = null;
  for (const click of [gclCookieClick(), fbcCookieClick()]) {
    if (click?.at === undefined || !isFresh(click.at, now)) continue;
    if (!best || click.at > (best.at as number)) best = click;
  }
  return best;
}

/**
 * The ONE click to forward. Each source hands over all its ids together or
 * none, so a `gclid` from this URL never travels next to an `fbclid` from an
 * older click (and `click_at` always describes the ids it goes with):
 *   1. the current URL (time: when first captured, else now);
 *   2. this tab's capture (`sessionStorage`);
 *   3. the last click of any visit (`localStorage`, under 90 days);
 *   4. the `_gcl_aw` / `_fbc` cookies.
 */
function resolveClick(current: CampaignParams, now: number): Click | null {
  const fromUrl = clickIdsOf(current);
  if (hasClick(fromUrl)) return { ids: fromUrl, at: knownClickTime(fromUrl, now) ?? now };

  const fromSession = clickIdsOf(readSession().params);
  if (hasClick(fromSession)) return { ids: fromSession, at: knownClickTime(fromSession, now) };

  return readStoredClick(now) ?? cookieClick(now);
}

/**
 * Captures the campaign of the page currently loading. Called once when the
 * bundle evaluates, so it runs on the ad's landing page even when no brick
 * mounts there.
 *
 * Only writes when the URL actually carries a campaign: on the later pages of
 * the visit the query is clean, and clearing there would undo the capture we
 * came for. A fresh campaign overwrites the previous one — within a session the
 * last ad clicked is the one that brought the visitor to this application.
 *
 * A click id is also kept in `localStorage` for later visits (see the module
 * comment), together with its capture time. The record holds ONE click: a new
 * one replaces it whole, so ids from different clicks never mix. A click
 * already seen keeps its first capture time and is not written again — it is
 * either the stored record itself, or this tab's own click that a newer one
 * from another tab has since replaced.
 */
export function captureCampaignParams(): CampaignParams {
  const current = readCampaignParams();
  if (Object.keys(current).length === 0) return readSession().params;

  const record: Record<string, unknown> = { ...current };
  const ids = clickIdsOf(current);
  if (hasClick(ids)) {
    const now = Date.now();
    const known = knownClickTime(ids, now);
    record.at = known ?? now;
    if (known === undefined) {
      writeJson(storage('localStorage'), CLICK_STORAGE_KEY, { ...ids, at: now });
    }
  }
  writeJson(storage('sessionStorage'), CAMPAIGN_STORAGE_KEY, record);
  return current;
}

/**
 * The campaign to forward to the iframe. Used at mount time.
 *
 * `utm_*`: what the current URL carries, or what was captured earlier in the
 * session — never older than the session.
 *
 * Click ids: resolved on their own, down to the stored click and the ad
 * cookies (`resolveClick`), plus `click_at` when the capture time is known.
 */
export function getCampaignParams(): ForwardedParams {
  const current = readCampaignParams();
  const base = Object.keys(current).length > 0 ? current : readSession().params;
  const out: ForwardedParams = {};
  for (const key of CAMPAIGN_KEYS) {
    const value = base[key];
    if (value && !isClickKey(key)) out[key] = value;
  }

  const click = resolveClick(current, Date.now());
  if (click) {
    Object.assign(out, click.ids);
    if (click.at !== undefined) out.click_at = String(Math.floor(click.at));
  }
  return out;
}

// Capture on script load, not on mount — by mount time the merchant's own
// navigation has usually stripped the params. Same reasoning as
// `SDK_SCRIPT_ORIGIN` in `core/finva`: the value only exists this early.
captureCampaignParams();
